import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { ExternalLink } from '../../components';

import css from './ListingPage.module.css';

// Ensure the stored value is an absolute URL so the link works even if the trainer
// entered it without a scheme (e.g. 'calendly.com/me').
const toAbsoluteUrl = url => {
  const trimmed = (url || '').trim();
  if (!trimmed) {
    return null;
  }
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Keep the button label on one line even when a coach has an unusually long
// first name. Names longer than this are cut and given an ellipsis so the
// button layout never breaks.
const MAX_FIRST_NAME_LENGTH = 20;
const truncateName = name => {
  const trimmed = (name || '').trim();
  return trimmed.length > MAX_FIRST_NAME_LENGTH
    ? `${trimmed.slice(0, MAX_FIRST_NAME_LENGTH)}…`
    : trimmed;
};

/**
 * Renders the trainer's external signup/booking link as a prominent button that
 * opens in a new tab. ExternalLink handles target="_blank" + rel="noopener noreferrer".
 *
 * The button label shows the coach's first name ("Train with Sarah"). When the
 * first name is missing it falls back to a generic label ("Train with this Coach")
 * so the button text is never blank.
 *
 * @param {Object} props
 * @param {string} props.text the URL stored in publicData
 * @param {string} [props.coachFirstName] the coach's first name, used in the label
 * @returns {JSX.Element|null}
 */
const SectionSignupLinkMaybe = props => {
  const { text, coachFirstName } = props;
  const href = toAbsoluteUrl(text);

  if (!href) {
    return null;
  }

  const safeFirstName = truncateName(coachFirstName);

  return (
    <section className={css.sectionSignupLink}>
      <ExternalLink href={href} className={css.signupLinkButton}>
        {safeFirstName ? (
          <FormattedMessage
            id="ListingPage.signupLinkButton"
            values={{ coachFirstName: safeFirstName }}
          />
        ) : (
          <FormattedMessage id="ListingPage.signupLinkButtonFallback" />
        )}
      </ExternalLink>
    </section>
  );
};

export default SectionSignupLinkMaybe;
